(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-interface-native-selector]';
  const normalize = (value) => String(value || '')
    .toLowerCase()
    .replace(/[™®]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const slugify = (value) => normalize(value).replace(/\s+/g, '-');
  const compactLinkSize = (value) => {
    const match = String(value || '').match(/\b(22|24)\s*mm\b/i) || String(value || '').match(/\b(22|24)\b/);
    return match ? `${match[1]}mm` : normalize(value).replace(/\s+/g, '');
  };
  const isThreeClock = (model) => {
    const crown = String(model?.crown_position || model?.crownPosition || '').trim();
    return /^3(?:\s|$|\s*o|\s*o’clock|\s*o'clock)/i.test(crown) || normalize(crown).includes('3 o');
  };

  const rememberFrameBase = (image) => {
    if (!image || image.dataset.b1FrameOriginalRemembered === 'true') return;
    image.dataset.b1FrameOriginalRemembered = 'true';
    image.dataset.b1FrameOriginalSrc = image.getAttribute('src') || '';
    image.dataset.b1FrameOriginalSrcset = image.getAttribute('srcset') || '';
    image.dataset.b1FrameOriginalSizes = image.getAttribute('sizes') || '';
  };

  const setFrameBaseImage = (image, alternateUrl) => {
    if (!image) return;
    rememberFrameBase(image);
    if (alternateUrl) {
      if (image.getAttribute('src') !== alternateUrl) image.setAttribute('src', alternateUrl);
      image.setAttribute('srcset', alternateUrl);
      image.removeAttribute('sizes');
      image.dataset.b1FrameUsing3Clock = 'true';
      return;
    }
    if (image.dataset.b1FrameUsing3Clock !== 'true') return;
    image.setAttribute('src', image.dataset.b1FrameOriginalSrc || '');
    if (image.dataset.b1FrameOriginalSrcset) image.setAttribute('srcset', image.dataset.b1FrameOriginalSrcset);
    else image.removeAttribute('srcset');
    if (image.dataset.b1FrameOriginalSizes) image.setAttribute('sizes', image.dataset.b1FrameOriginalSizes);
    else image.removeAttribute('sizes');
    delete image.dataset.b1FrameUsing3Clock;
  };

  const parseData = (sectionId) => {
    const node = document.getElementById(`interface-pdp-data-${sectionId}`);
    if (!node) return {};
    try { return JSON.parse(node.textContent || '{}'); } catch (error) {
      console.warn('[B1 Interface] Unable to parse interface data.', error);
      return {};
    }
  };

  const selectedVariantId = (sectionId) => {
    const form = document.getElementById(`product-form-${sectionId}`);
    const input = form?.querySelector('[name="id"]');
    return String(input?.value || '');
  };

  const variantOption = (variant, zeroBasedIndex) => {
    if (!variant || !Number.isInteger(Number(zeroBasedIndex))) return '';
    return variant[`option${Number(zeroBasedIndex) + 1}`] || '';
  };

  const findVariant = (data, sectionId) => {
    const id = selectedVariantId(sectionId);
    return (data.variants || []).find((variant) => String(variant.id) === id) || (data.variants || [])[0] || null;
  };

  const selectedSetValue = (root) => {
    const frameChoice = root?.closest('product-info')?.querySelector('[data-b1-bandware-choice].is-selected');
    if (frameChoice) return frameChoice.dataset.b1BandwareChoice === 'add' ? 'complete' : 'frame-only';
    const linkChoice = root?.closest('product-info')?.querySelector('[data-b1-link-bandware-choice].is-selected');
    if (linkChoice) return linkChoice.dataset.b1LinkBandwareChoice === 'complete' ? 'complete' : 'link-only';
    return new URL(window.location.href).searchParams.get('set') || '';
  };

  const updateFamilyLinks = (root, key, value) => {
    const setValue = selectedSetValue(root);
    root.closest('[data-product-options-stack]')?.querySelectorAll('#swatches a[href]').forEach((anchor) => {
      const raw = anchor.dataset.b1InterfaceBaseHref || anchor.getAttribute('href');
      if (!raw) return;
      if (!anchor.dataset.b1InterfaceBaseHref) anchor.dataset.b1InterfaceBaseHref = raw;
      let url;
      try { url = new URL(anchor.dataset.b1InterfaceBaseHref, window.location.origin); } catch (_) { return; }
      if (url.origin !== window.location.origin) return;
      url.searchParams.delete(key === 'model' ? 'device' : 'model');
      url.searchParams.delete('size');
      if (value) url.searchParams.set(key, value); else url.searchParams.delete(key);
      if (setValue) url.searchParams.set('set', setValue);
      anchor.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    });
  };

  const writeStateUrl = (root, key, value, push = false) => {
    const url = new URL(window.location.href);
    const setValue = selectedSetValue(root);
    url.searchParams.delete(key === 'model' ? 'device' : 'model');
    url.searchParams.delete('size');
    if (value) url.searchParams.set(key, value); else url.searchParams.delete(key);
    if (setValue) url.searchParams.set('set', setValue);
    const next = `${url.pathname}${url.search}${url.hash}`;
    if (push) history.pushState({ b1InterfaceState: value || null }, '', next);
    else history.replaceState(history.state || {}, '', next);
  };

  const ensureFrameOverlay = (mediaItem, url) => {
    if (!mediaItem) return;
    let overlay = mediaItem.querySelector(':scope > .b1-frame-gallery__overlay');
    if (!url) {
      overlay?.remove();
      mediaItem.classList.remove('b1-frame-gallery--active');
      return;
    }

    mediaItem.classList.add('b1-frame-gallery--active');
    if (!overlay) {
      overlay = document.createElement('img');
      overlay.className = 'b1-frame-gallery__overlay';
      overlay.alt = '';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.decoding = 'async';
      overlay.loading = 'eager';
      mediaItem.appendChild(overlay);
    }
    if (overlay.getAttribute('src') !== url) overlay.setAttribute('src', url);
  };

  class InterfaceNativeSelector {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.family = root.dataset.interfaceFamily || 'link';
      this.productInfo = root.closest('product-info');
      this.data = parseData(this.sectionId);
      this.label = root.querySelector('[data-interface-selected-label]');
      this.details = root.querySelector('details');
      this.selectedModel = null;
      this.selectedDevice = null;
      this.variantUnsubscriber = null;

      if (this.family === 'frame') this.initFrame();
      else this.initLink();

      this.bindCommon();
      this.bindRailDiscovery();
    }

    revealSelectedChoice() {
      if (!window.matchMedia?.('(max-width: 749px)').matches) return;
      const rail = this.root.querySelector('.b1-interface-native-selector__slider');
      const selected = rail?.querySelector('.product-option-card.is-selected, .product-option-card[aria-pressed="true"]');
      if (!rail || !selected) return;
      requestAnimationFrame(() => {
        const maxLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
        const idealLeft = selected.offsetLeft - Math.max(0, (rail.clientWidth - selected.offsetWidth) / 2);
        rail.scrollTo({ left: Math.max(0, Math.min(maxLeft, idealLeft)), behavior: 'auto' });
      });
    }

    bindRailDiscovery() {
      if (!this.details || this.details.dataset.interfaceRailDiscoveryBound === 'true') return;
      this.details.dataset.interfaceRailDiscoveryBound = 'true';
      this.details.addEventListener('toggle', () => {
        if (this.details.open) this.revealSelectedChoice();
      });
    }

    bindCommon() {
      this.root.querySelectorAll('[data-interface-model-choice], [data-interface-device-choice]').forEach((choice) => {
        const previewLabel = choice.dataset.modelName || choice.dataset.deviceSize || '';
        const preview = () => { if (this.label && previewLabel) this.label.textContent = previewLabel; };
        const restore = () => {
          const selected = this.family === 'frame' ? this.selectedModel?.name : this.selectedDevice?.device_size;
          if (this.label && selected) this.label.textContent = selected;
        };
        choice.addEventListener('mouseenter', preview);
        choice.addEventListener('focus', preview);
        choice.addEventListener('mouseleave', restore);
        choice.addEventListener('blur', restore);
      });

      this.productInfo?.addEventListener('brilinton:product-gallery:dom-updated', (event) => {
        if (event.target !== this.productInfo) return;

        // ProductInfo replaces the modal media DOM during a Shopify variant
        // refresh. Re-capture the current live gallery/modal slots and let the
        // active Interface context perform the one final gallery transaction.
        if (this.family === 'frame') {
          this.captureFrameGallery();
          this.frameGalleryKey = '';
          if (this.selectedModel) this.applyFrameGallery(this.selectedModel);
          return;
        }

        this.captureLinkGallery();
        this.linkGalleryKey = '';
        this.applyLinkGallery(this.selectedDevice);
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
          if (String(data?.sectionId || '') !== String(this.sectionId)) return;
          if (this.family === 'link' && this.selectedDevice) {
            requestAnimationFrame(() => {
              writeStateUrl(this.root, 'device', this.selectedDevice.slug || slugify(this.selectedDevice.device_size));
              updateFamilyLinks(this.root, 'device', this.selectedDevice.slug || slugify(this.selectedDevice.device_size));
              this.applyLinkGallery(this.selectedDevice);
            });
          } else if (this.family === 'link') {
            requestAnimationFrame(() => this.enforceRequiredDeviceSelection());
          } else if (this.family === 'frame' && this.selectedModel) {
            requestAnimationFrame(() => this.applyFrameGallery(this.selectedModel));
          }
        });
      }
    }

    setSelectedChoice(selector, predicate) {
      this.root.querySelectorAll(selector).forEach((choice) => {
        const selected = Boolean(predicate(choice));
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    captureFrameGallery() {
      this.frameMediaGallery = document.getElementById(`MediaGallery-${this.sectionId}`);
      this.frameGalleryViewer = document.getElementById(`GalleryViewer-${this.sectionId}`);
      this.frameGalleryList = document.getElementById(`Slider-Gallery-${this.sectionId}`);
      this.frameThumbnailViewer = document.getElementById(`GalleryThumbnails-${this.sectionId}`);
      this.frameThumbnailList = this.frameThumbnailViewer?.querySelector('ul') || null;
      this.frameModalContent = document.querySelector(`#ProductModal-${this.sectionId} .product-media-modal__content`);
      this.frameGallerySlots = this.captureFrameSlots(
        Array.from(this.frameGalleryList?.querySelectorAll(':scope > [data-b1-frame-context-slot]') || []),
        '.product__modal-opener--image img, .product__media img'
      );
      this.frameThumbnailSlots = this.captureFrameSlots(
        Array.from(this.frameThumbnailList?.querySelectorAll(':scope > [data-b1-frame-context-thumbnail]') || []),
        'img'
      );
      this.frameModalSlots = this.captureFrameSlots(
        Array.from(this.frameModalContent?.querySelectorAll(':scope > [data-b1-frame-context-modal]') || []),
        null
      );
    }

    captureFrameSlots(nodes, imageSelector) {
      return nodes.map((node) => {
        const image = imageSelector ? node.querySelector(imageSelector) : (node.matches?.('img') ? node : null);
        if (!image) return null;
        const original = {};
        Array.from(image.attributes || []).forEach((attribute) => { original[attribute.name] = attribute.value; });
        const container = node.querySelector?.('.product-media-container') || null;
        return {
          node,
          image,
          original,
          container,
          originalContainerStyle: container?.getAttribute('style') || '',
        };
      }).filter(Boolean);
    }

    restoreFrameImage(slot) {
      if (!slot?.image) return;
      Array.from(slot.image.attributes || []).forEach((attribute) => slot.image.removeAttribute(attribute.name));
      Object.entries(slot.original || {}).forEach(([name, value]) => slot.image.setAttribute(name, value));
      slot.image.style.removeProperty('background-image');
      slot.image.style.removeProperty('background-position');
      slot.image.style.removeProperty('background-repeat');
      slot.image.style.removeProperty('background-size');
      if (slot.container) {
        if (slot.originalContainerStyle) slot.container.setAttribute('style', slot.originalContainerStyle);
        else slot.container.removeAttribute('style');
      }
    }

    projectFrameImage(slot, item, eager = false) {
      if (!slot?.image || !item?.src) return;
      this.restoreFrameImage(slot);
      slot.image.setAttribute('src', item.src);
      slot.image.setAttribute('srcset', item.src);
      slot.image.removeAttribute('sizes');
      slot.image.setAttribute('alt', item.alt || '');
      slot.image.setAttribute('loading', eager ? 'eager' : 'lazy');
      slot.image.setAttribute('decoding', 'async');
      if (item.width) slot.image.setAttribute('width', String(item.width));
      if (item.height) slot.image.setAttribute('height', String(item.height));
      const width = Number(item.width || 0);
      const height = Number(item.height || 0);
      const ratio = width > 0 && height > 0 ? width / height : 0;
      if (slot.container && Number.isFinite(ratio) && ratio > 0) {
        const value = String(ratio);
        slot.container.style.setProperty('--ratio', value);
        slot.container.style.setProperty('--preview-ratio', value);
        slot.container.style.setProperty('--aspect-ratio', value);
      }
    }

    projectFrameComposite(slot, item, eager = false) {
      if (!slot?.image || !item?.src) return;
      if (!item.overlay) {
        this.projectFrameImage(slot, item, eager);
        return;
      }
      this.restoreFrameImage(slot);
      slot.image.setAttribute('src', item.overlay);
      slot.image.setAttribute('srcset', item.overlay);
      slot.image.removeAttribute('sizes');
      slot.image.setAttribute('alt', item.alt || '');
      slot.image.setAttribute('loading', eager ? 'eager' : 'lazy');
      slot.image.setAttribute('decoding', 'async');
      slot.image.style.backgroundImage = `url("${String(item.src).replace(/"/g, '%22')}")`;
      slot.image.style.backgroundPosition = 'center';
      slot.image.style.backgroundRepeat = 'no-repeat';
      slot.image.style.backgroundSize = 'cover';
      if (item.width) slot.image.setAttribute('width', String(item.width));
      if (item.height) slot.image.setAttribute('height', String(item.height));
    }

    frameGalleryItems(model) {
      if (selectedSetValue(this.root) !== 'complete') return [];
      const source = this.data.frameSetGallery || {};
      const twelve = Array.isArray(source.twelveClock) ? source.twelveClock : [];
      const three = Array.isArray(source.threeClock) ? source.threeClock : [];
      const base = isThreeClock(model) && three.length ? three : twelve;
      const overlays = Array.isArray(model?.frame_gallery) ? model.frame_gallery : [];
      return base.filter((item) => item?.src).map((item, index) => ({
        ...item,
        overlay: overlays[index] || '',
        alt: `${this.data.interfaceTitle || 'B¹ Frame™'} with ${model?.name || 'Royal Pop'}`,
      }));
    }

    applyFrameItems(slots, items, { main = false } = {}) {
      (slots || []).forEach((slot, index) => {
        const item = items[index];
        if (item?.src) {
          if (main) {
            this.projectFrameImage(slot, item, index === 0);
            ensureFrameOverlay(slot.node, item.overlay || '');
          } else {
            this.projectFrameComposite(slot, item, index === 0);
          }
          slot.node.hidden = false;
          slot.node.setAttribute('aria-hidden', 'false');
        } else {
          ensureFrameOverlay(slot.node, '');
          this.restoreFrameImage(slot);
          slot.node.hidden = true;
          slot.node.setAttribute('aria-hidden', 'true');
        }
      });
    }

    refreshFrameGallery(items, galleryManaged = false) {
      void this.frameGalleryList?.offsetWidth;
      const firstContext = this.frameGallerySlots?.find((slot) => !slot.node.hidden)?.node;
      const firstNative = this.frameGalleryList?.querySelector(':scope > .product__media-item:not([data-b1-frame-context-slot]):not([hidden])');
      const firstTarget = firstContext || firstNative;
      const firstMediaId = firstTarget?.dataset?.mediaId;
      const visibleTotal = items.length
        + Array.from(this.frameGalleryList?.querySelectorAll(':scope > .product__media-item:not([data-b1-frame-context-slot]):not([hidden])') || []).length;

      if (galleryManaged) {
        window.BrilintonProductGallery.endContentUpdate(this.frameMediaGallery, {
          reason: 'interface-frame-context',
          activeMediaId: firstMediaId,
          resetPosition: true,
          contentChanged: true,
        });
        return;
      }

      this.frameGalleryViewer?.resetPages?.();
      this.frameThumbnailViewer?.resetPages?.();
      this.frameGalleryList?.querySelectorAll(':scope > .product__media-item.is-active').forEach((node) => {
        node.classList.toggle('is-active', node === firstTarget);
      });
      if (firstMediaId) this.frameMediaGallery?.setActiveMedia?.(firstMediaId, false, { scrollPage: false });
      this.frameGalleryViewer?.querySelectorAll('.slider-counter--total').forEach((node) => { node.textContent = String(Math.max(visibleTotal, 1)); });
      const customCounter = this.frameGalleryViewer?.querySelector('#slideCounter');
      if (customCounter) customCounter.textContent = `1 / ${Math.max(visibleTotal, 1)}`;
    }

    initFrame() {
      this.captureFrameGallery();
      const models = this.data.royalModels || [];
      if (!models.length) return;
      const params = new URL(window.location.href).searchParams;
      const rememberedRoyal = window.BrilintonDeviceContext?.get('royalPop');
      const requested = params.get('model') || (rememberedRoyal?.modelSlug || rememberedRoyal?.handle) || '';
      const numeric = /^\d+$/.test(requested || '') ? Number(requested) : -1;
      this.selectedModel = (numeric >= 0 ? models[numeric] : null)
        || models.find((model) => normalize(model.slug || model.name) === normalize(requested))
        || models.find((model) => normalize(model.name) === 'ocho negro')
        || models[0];

      this.applyFrameModel(this.selectedModel, { updateUrl: false, emit: false });

      this.root.querySelectorAll('[data-interface-model-choice]').forEach((choice) => {
        choice.addEventListener('click', () => {
          const index = Number.parseInt(choice.dataset.modelIndex || '0', 10);
          const model = models.find((entry) => normalize(entry.slug || entry.name) === normalize(choice.dataset.modelSlug)) || models[index];
          if (!model) return;
          this.applyFrameModel(model, { updateUrl: true, emit: true, push: true });
          if (this.details) this.details.open = false;
        });
      });

    }

    applyFrameGallery(model) {
      const firstSlot = this.frameGallerySlots?.[0]?.node;
      if (!firstSlot || !firstSlot.isConnected) this.captureFrameGallery();
      const items = this.frameGalleryItems(model);
      const key = `${selectedSetValue(this.root)}|${model?.slug || model?.name || ''}|${items.map((item) => `${item.src}:${item.overlay || ''}`).join('|')}`;
      if (this.frameGalleryKey === key && (items.length === 0 || this.frameGallerySlots?.some((slot) => !slot.node.hidden))) return;
      this.frameGalleryKey = key;

      const galleryManaged = window.BrilintonProductGallery?.beginContentUpdate?.(this.frameMediaGallery, {
        reason: 'interface-frame-context',
      }) === true;

      try {
        // Native product media belongs to FRAME ONLY. Remove any legacy composition
        // state and project complete-set imagery only into dedicated context slots.
        const nativeFrameMedia = [
          ...Array.from(this.frameGalleryList?.querySelectorAll(':scope > .product__media-item:not([data-b1-frame-context-slot])') || []),
          ...Array.from(this.frameModalContent?.querySelectorAll(':scope > :not([data-b1-frame-context-modal])') || []),
        ];
        nativeFrameMedia.forEach((item) => {
          const baseImage = item?.querySelector?.('img:not(.b1-frame-gallery__overlay)') || (item.matches?.('img') ? item : null);
          setFrameBaseImage(baseImage, '');
          ensureFrameOverlay(item, '');
        });

        this.applyFrameItems(this.frameGallerySlots, items, { main: true });
        this.applyFrameItems(this.frameThumbnailSlots, items);
        this.applyFrameItems(this.frameModalSlots, items);
      } finally {
        this.refreshFrameGallery(items, galleryManaged);
      }
    }

    applyFrameModel(model, { updateUrl = true, emit = true, push = false } = {}) {
      if (!model) return;
      this.selectedModel = model;
      const slug = model.slug || slugify(model.name);
      this.setSelectedChoice('[data-interface-model-choice]', (choice) => normalize(choice.dataset.modelSlug) === normalize(slug));
      if (this.label) this.label.textContent = model.name || 'Royal Pop';
      this.applyFrameGallery(model);
      updateFamilyLinks(this.root, 'model', slug);
      if (updateUrl) writeStateUrl(this.root, 'model', slug, push);
      if (emit) window.BrilintonDeviceContext?.rememberRoyalPop(slug, 'interface-native');
      if (emit) {
        document.dispatchEvent(new CustomEvent('royal-pop:model-change', {
          detail: { modelSlug: slug, modelIndex: (this.data.royalModels || []).indexOf(model), source: 'interface-native' }
        }));
      }
    }

    captureLinkGallery() {
      this.linkMediaGallery = document.getElementById(`MediaGallery-${this.sectionId}`);
      this.linkGalleryViewer = document.getElementById(`GalleryViewer-${this.sectionId}`);
      this.linkGalleryList = document.getElementById(`Slider-Gallery-${this.sectionId}`);
      this.linkThumbnailViewer = document.getElementById(`GalleryThumbnails-${this.sectionId}`);
      this.linkThumbnailList = this.linkThumbnailViewer?.querySelector('ul') || null;
      this.linkModalContent = document.querySelector(`#ProductModal-${this.sectionId} .product-media-modal__content`);
      this.linkGallerySlots = this.captureLinkSlots(
        Array.from(this.linkGalleryList?.querySelectorAll(':scope > [data-b1-link-context-slot]') || []),
        '.product__modal-opener--image img, .product__media img'
      );
      this.linkThumbnailSlots = this.captureLinkSlots(
        Array.from(this.linkThumbnailList?.querySelectorAll(':scope > [data-b1-link-context-thumbnail]') || []),
        'img'
      );
      this.linkModalSlots = this.captureLinkSlots(
        Array.from(this.linkModalContent?.querySelectorAll(':scope > [data-b1-link-context-modal]') || []),
        null
      );
    }

    captureLinkSlots(nodes, imageSelector) {
      return nodes.map((node) => {
        const image = imageSelector ? node.querySelector(imageSelector) : (node.matches?.('img') ? node : null);
        if (!image) return null;
        const original = {};
        Array.from(image.attributes || []).forEach((attribute) => { original[attribute.name] = attribute.value; });
        const container = node.querySelector?.('.product-media-container') || null;
        return {
          node,
          image,
          original,
          container,
          originalContainerStyle: container?.getAttribute('style') || '',
        };
      }).filter(Boolean);
    }

    restoreLinkImage(slot) {
      if (!slot?.image) return;
      Array.from(slot.image.attributes || []).forEach((attribute) => slot.image.removeAttribute(attribute.name));
      Object.entries(slot.original || {}).forEach(([name, value]) => slot.image.setAttribute(name, value));
      if (slot.container) {
        if (slot.originalContainerStyle) slot.container.setAttribute('style', slot.originalContainerStyle);
        else slot.container.removeAttribute('style');
      }
    }

    currentLinkFinish() {
      const variant = findVariant(this.data, this.sectionId);
      return variantOption(variant, Number(this.data.optionIdx?.finish || 0));
    }

    linkGalleryProfile() {
      const finish = this.currentLinkFinish();
      return (this.data.linkGalleryProfiles || []).find((profile) => normalize(profile.finish) === normalize(finish))
        || (this.data.linkGalleryProfiles || []).find((profile) => normalize(profile.finish).replace(/\s+/g, '') === normalize(finish).replace(/\s+/g, ''))
        || null;
    }

    linkGalleryItems(device) {
      if (selectedSetValue(this.root) !== 'complete') return [];
      const profile = this.linkGalleryProfile();
      if (!profile) return [];
      const ultra = /49/.test(String(device?.device_size || ''));
      return (ultra ? (profile.appleUltra || []) : (profile.appleStandard || [])).filter((item) => item?.src);
    }

    projectLinkImage(slot, item, eager = false) {
      if (!slot?.image || !item?.src) return;
      this.restoreLinkImage(slot);
      slot.image.setAttribute('src', item.src);
      slot.image.setAttribute('srcset', item.src);
      slot.image.removeAttribute('sizes');
      slot.image.setAttribute('alt', item.alt || '');
      slot.image.setAttribute('loading', eager ? 'eager' : 'lazy');
      slot.image.setAttribute('decoding', 'async');
      if (item.width) slot.image.setAttribute('width', String(item.width));
      if (item.height) slot.image.setAttribute('height', String(item.height));

      const width = Number(item.width || 0);
      const height = Number(item.height || 0);
      const ratio = width > 0 && height > 0 ? width / height : 0;
      if (slot.container && Number.isFinite(ratio) && ratio > 0) {
        const value = String(ratio);
        slot.container.style.setProperty('--ratio', value);
        slot.container.style.setProperty('--preview-ratio', value);
        slot.container.style.setProperty('--aspect-ratio', value);
      }
    }

    applyLinkItems(slots, items) {
      (slots || []).forEach((slot, index) => {
        const item = items[index];
        if (item?.src) {
          this.projectLinkImage(slot, item, index === 0);
          slot.node.hidden = false;
          slot.node.setAttribute('aria-hidden', 'false');
        } else {
          this.restoreLinkImage(slot);
          slot.node.hidden = true;
          slot.node.setAttribute('aria-hidden', 'true');
        }
      });
    }

    applyLinkGallery(device = this.selectedDevice) {
      const firstSlot = this.linkGallerySlots?.[0]?.node;
      if (!firstSlot || !firstSlot.isConnected) this.captureLinkGallery();
      const items = device ? this.linkGalleryItems(device) : [];
      const key = `${selectedSetValue(this.root)}|${this.currentLinkFinish()}|${device?.device_size || 'unselected'}|${items.map((item) => item.src).join('|')}`;
      if (this.linkGalleryKey === key && this.linkGallerySlots?.some((slot) => !slot.node.hidden)) return;
      this.linkGalleryKey = key;
      const galleryManaged = window.BrilintonProductGallery?.beginContentUpdate?.(this.linkMediaGallery, {
        reason: 'interface-link-context',
      }) === true;

      try {
        this.applyLinkItems(this.linkGallerySlots, items);
        this.applyLinkItems(this.linkThumbnailSlots, items);
        this.applyLinkItems(this.linkModalSlots, items);
      } finally {
        void this.linkGalleryList?.offsetWidth;
        const firstContext = this.linkGallerySlots?.find((slot) => !slot.node.hidden)?.node;
        const firstNative = this.linkGalleryList?.querySelector(':scope > .product__media-item:not([data-b1-link-context-slot]):not([hidden])');
        const firstTarget = firstContext || firstNative;
        const firstMediaId = firstTarget?.dataset?.mediaId;
        const visibleTotal = items.length
          + Array.from(this.linkGalleryList?.querySelectorAll(':scope > .product__media-item:not([data-b1-link-context-slot]):not([hidden])') || []).length;

        if (galleryManaged) {
          window.BrilintonProductGallery.endContentUpdate(this.linkMediaGallery, {
            reason: 'interface-link-context',
            activeMediaId: firstMediaId,
            resetPosition: true,
            contentChanged: true,
          });
        } else {
          this.linkGalleryViewer?.resetPages?.();
          this.linkThumbnailViewer?.resetPages?.();
          this.linkGalleryList?.querySelectorAll(':scope > .product__media-item.is-active').forEach((node) => {
            node.classList.toggle('is-active', node === firstTarget);
          });
          if (firstMediaId) this.linkMediaGallery?.setActiveMedia?.(firstMediaId, false, { scrollPage: false });
          this.linkGalleryViewer?.querySelectorAll('.slider-counter--total').forEach((node) => { node.textContent = String(Math.max(visibleTotal, 1)); });
          const customCounter = this.linkGalleryViewer?.querySelector('#slideCounter');
          if (customCounter) customCounter.textContent = `1 / ${Math.max(visibleTotal, 1)}`;
        }
      }
    }

    initLink() {
      this.captureLinkGallery();
      const devices = this.data.appleSizes || [];
      if (!devices.length) return;
      const params = new URL(window.location.href).searchParams;
      const rememberedApple = window.BrilintonDeviceContext?.get('appleWatch');
      const requested = params.get('device') || params.get('size') || rememberedApple?.handle || rememberedApple?.size;
      this.selectedDevice = requested
        ? devices.find((device) => normalize(device.slug || device.device_size) === normalize(requested)) || null
        : null;

      if (this.selectedDevice) {
        this.applyDevice(this.selectedDevice, { selectVariant: true, updateUrl: false, emit: false });
      } else {
        this.applyLinkGallery(null);
        this.enforceRequiredDeviceSelection();
      }

      this.root.querySelectorAll('[data-interface-device-choice]').forEach((choice) => {
        choice.addEventListener('click', () => {
          const index = Number.parseInt(choice.dataset.deviceIndex || '0', 10);
          const device = devices.find((entry) => normalize(entry.slug || entry.device_size) === normalize(choice.dataset.deviceSlug)) || devices[index];
          if (!device) return;
          this.applyDevice(device, { selectVariant: true, updateUrl: true, emit: true, push: true });
          if (this.details) this.details.open = false;
        });
      });
    }

    enforceRequiredDeviceSelection() {
      if (this.family !== 'link' || this.selectedDevice) return;
      if (this.label) this.label.textContent = 'SELECT';
      const productForm = this.productInfo?.querySelector('product-form');
      productForm?.toggleSubmitButton?.(true, 'Select Size');
    }

    technicalOptionHolder() {
      const position = Number(this.data.optionIdx?.size || 0) + 1;
      const variantSelects = this.productInfo?.querySelector('variant-selects');
      if (!variantSelects) return null;
      return variantSelects.querySelector(`[data-option-position="${position}"]`)
        || Array.from(variantSelects.querySelectorAll('[data-option-position]')).find((holder) =>
          Array.from(holder.querySelectorAll('input, option')).some((node) => /\b(?:22|24)\s*mm\b/i.test(node.value || node.textContent || ''))
        )
        || null;
    }

    selectLinkVariant(linkSize, forceDispatch = false) {
      const holder = this.technicalOptionHolder();
      if (!holder) {
        console.warn('[B1 Link] Technical 22mm/24mm variant option was not found.');
        return false;
      }
      const desired = compactLinkSize(linkSize);
      const radio = Array.from(holder.querySelectorAll('input[type="radio"]')).find((input) => compactLinkSize(input.value) === desired);
      if (radio) {
        const changed = !radio.checked;
        radio.checked = true;
        if (changed || forceDispatch) radio.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      const select = holder.querySelector('select');
      if (select) {
        const option = Array.from(select.options).find((entry) => compactLinkSize(entry.value) === desired);
        if (option) {
          const changed = select.value !== option.value;
          select.value = option.value;
          if (changed || forceDispatch) select.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }

      console.warn(`[B1 Link] No Shopify variant option matches ${linkSize}.`);
      return false;
    }

    applyDevice(device, { selectVariant = true, updateUrl = true, emit = true, push = false } = {}) {
      if (!device) return;
      const firstSelection = !this.selectedDevice;
      if (selectVariant && !this.selectLinkVariant(device.b1_link_size, firstSelection)) return;
      this.selectedDevice = device;
      const slug = device.slug || slugify(device.device_size);
      this.setSelectedChoice('[data-interface-device-choice]', (choice) => normalize(choice.dataset.deviceSlug) === normalize(slug));
      if (this.label) this.label.textContent = device.device_size || 'Apple Watch';
      this.applyLinkGallery(device);
      updateFamilyLinks(this.root, 'device', slug);
      if (emit) window.BrilintonDeviceContext?.rememberAppleWatch({ handle: slug, size: device.device_size }, 'interface-native');
      if (updateUrl) {
        // ProductInfo may replace the URL again after its variant fetch; the
        // variantChange subscription restores the customer-facing device state.
        writeStateUrl(this.root, 'device', slug, push);
      }
      if (emit) {
        document.dispatchEvent(new CustomEvent('b1-interface:device-change', {
          detail: {
            deviceSize: device.device_size,
            deviceSlug: slug,
            linkSize: device.b1_link_size,
            source: 'interface-native'
          }
        }));
      }
    }

    applyExternalModel(slug) {
      if (this.family !== 'frame' || !slug) return;
      const model = (this.data.royalModels || []).find((entry) => normalize(entry.slug || entry.name) === normalize(slug));
      if (model && model !== this.selectedModel) this.applyFrameModel(model, { updateUrl: true, emit: false });
    }

    applyExternalDevice(deviceSize, deviceSlug) {
      if (this.family !== 'link') return;
      const device = (this.data.appleSizes || []).find((entry) =>
        normalize(entry.slug || entry.device_size) === normalize(deviceSlug || deviceSize)
        || normalize(entry.device_size) === normalize(deviceSize)
      );
      if (device && device !== this.selectedDevice) this.applyDevice(device, { selectVariant: true, updateUrl: true, emit: false });
    }
  }

  const instances = () => Array.from(document.querySelectorAll(ROOT_SELECTOR))
    .map((root) => root.__b1InterfaceNativeSelector)
    .filter(Boolean);

  const init = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.__b1InterfaceNativeSelector) return;
      root.__b1InterfaceNativeSelector = new InterfaceNativeSelector(root);
    });
  };

  document.addEventListener('royal-pop:model-change', (event) => {
    if (event.detail?.source === 'interface-native') return;
    instances().forEach((instance) => instance.applyExternalModel(event.detail?.modelSlug));
  });

  document.addEventListener('b1-interface:device-change', (event) => {
    if (event.detail?.source === 'interface-native') return;
    instances().forEach((instance) => instance.applyExternalDevice(event.detail?.deviceSize, event.detail?.deviceSlug));
  });

  document.addEventListener('b1-frame:bandware-change', () => {
    instances().forEach((instance) => {
      if (instance.family === 'frame' && instance.selectedModel) {
        instance.frameGalleryKey = '';
        instance.applyFrameGallery(instance.selectedModel);
      }
    });
  });

  document.addEventListener('b1-link:bandware-change', () => {
    instances().forEach((instance) => {
      if (instance.family === 'link') {
        instance.linkGalleryKey = '';
        instance.applyLinkGallery(instance.selectedDevice);
      }
    });
  });

  document.addEventListener('shopify:section:load', init);
  window.addEventListener('popstate', () => {
    instances().forEach((instance) => {
      if (instance.family === 'frame') {
        const slug = new URL(window.location.href).searchParams.get('model');
        if (slug) instance.applyExternalModel(slug);
      } else {
        const params = new URL(window.location.href).searchParams;
        const slug = params.get('device') || params.get('size');
        if (slug) instance.applyExternalDevice(slug, slug);
      }
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
